import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useOutletContext, useParams } from 'react-router-dom';
import cn from '@/utils/cn';
import { Field, PanelFooter, hintClass, panelClass } from '../../shared/formPanel';
import SPACheckbox from '../../shared/SPACheckbox';
import SPAInput, { spaInputClass } from '../../shared/SPAInput';
import SPASectionHeading from '../../shared/SPASectionHeading';
import { useSPAToast } from '../../shared/SPAToast';
import { EditableUnit, useUpdateUnit } from './useUnit';

interface FormValues {
  automatic_translation_enabled: boolean;
  call_out_color: string;
  call_out_text: string;
  color: string;
  compelling_question: string;
  compelling_question_es: string;
  phenomenon: string;
  publish: boolean;
  storyline: string;
  storyline_es: string;
  title: string;
  title_es: string;
}

const toFormValues = (unit: EditableUnit): FormValues => ({
  automatic_translation_enabled: unit.automatic_translation_enabled,
  call_out_color: unit.call_out_color ?? '',
  call_out_text: unit.call_out_text ?? '',
  color: unit.color ?? '',
  compelling_question: unit.compelling_question ?? '',
  compelling_question_es: unit.compelling_question_es ?? '',
  phenomenon: unit.phenomenon ?? '',
  publish: unit.publish,
  storyline: unit.storyline ?? '',
  storyline_es: unit.storyline_es ?? '',
  title: unit.title,
  title_es: unit.title_es ?? '',
});

const textareaClass = cn(spaInputClass, 'tw-h-24 tw-resize-y tw-py-2');

const swatchClass = cn(
  'tw-h-[2.125rem] tw-w-[2.125rem] tw-shrink-0 tw-rounded',
  'tw-border tw-border-solid tw-border-spa-border-input',
);

const UnitInformationPanel = () => {
  const unit = useOutletContext<EditableUnit>();
  const { programId } = useParams<{ programId: string }>();
  const spaToast = useSPAToast();
  const updateUnit = useUpdateUnit(Number(programId), unit.id);
  const values = useMemo(() => toFormValues(unit), [unit]);
  const {
    formState: { errors }, handleSubmit, register, watch,
  } = useForm<FormValues>({ values });

  const save = (formValues: FormValues) => {
    updateUnit.mutate({ unit: formValues }, {
      onSuccess: () => spaToast.success('Saved unit information.'),
    });
  };

  return (
    <form noValidate className={panelClass} onSubmit={handleSubmit(save)}>
      <SPASectionHeading className="tw-mb-5" title="Unit Information" />

      <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 md:tw-grid-cols-2">
        <Field required error={errors.title?.message} htmlFor="unit_title" label="Title">
          <SPAInput id="unit_title" {...register('title', { required: 'Title is required.' })} />
        </Field>
        <Field htmlFor="unit_title_es" label="Spanish Title">
          <SPAInput id="unit_title_es" {...register('title_es')} />
        </Field>

        <div className="md:tw-col-span-2">
          <SPACheckbox {...register('automatic_translation_enabled')}>Automatic translation enabled</SPACheckbox>
        </div>

        <Field htmlFor="unit_compelling_question" label="Compelling Question">
          <textarea className={textareaClass} id="unit_compelling_question" {...register('compelling_question')} />
        </Field>
        <Field htmlFor="unit_compelling_question_es" label="Spanish Compelling Question">
          <textarea
            className={textareaClass}
            id="unit_compelling_question_es"
            {...register('compelling_question_es')}
          />
        </Field>

        <Field htmlFor="unit_storyline" label="Storyline">
          <textarea className={textareaClass} id="unit_storyline" {...register('storyline')} />
        </Field>
        <Field htmlFor="unit_storyline_es" label="Spanish Storyline">
          <textarea className={textareaClass} id="unit_storyline_es" {...register('storyline_es')} />
        </Field>

        <Field className="md:tw-col-span-2" htmlFor="unit_phenomenon" label="Phenomenon">
          <textarea className={textareaClass} id="unit_phenomenon" {...register('phenomenon')} />
        </Field>

        <Field hint="Six hex characters, 0–9 and a–f." htmlFor="unit_color" label="Color">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span
              aria-hidden="true"
              className={swatchClass}
              style={{ backgroundColor: `#${watch('color') || '522e91'}` }}
            />
            <SPAInput id="unit_color" maxLength={6} {...register('color')} />
          </div>
        </Field>
        <div className="tw-flex tw-items-end">
          <SPACheckbox {...register('publish')}>Publish to users?</SPACheckbox>
        </div>

        <Field
          hint="Up to 12 characters, shown as a badge on the unit for students."
          htmlFor="unit_call_out_text"
          label="Call Out Text"
        >
          <SPAInput id="unit_call_out_text" maxLength={12} {...register('call_out_text')} />
        </Field>
        <Field htmlFor="unit_call_out_color" label="Call Out Color">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span
              aria-hidden="true"
              className={swatchClass}
              style={{ backgroundColor: `#${watch('call_out_color') || 'd9482b'}` }}
            />
            <SPAInput id="unit_call_out_color" maxLength={6} {...register('call_out_color')} />
          </div>
          <span className={hintClass}>Six hex characters, 0–9 and a–f.</span>
        </Field>
      </div>

      <PanelFooter update={updateUnit} />
    </form>
  );
};

export default UnitInformationPanel;
